/**
 * Comprehensive field-level validation utilities
 * Provides contextual validation for all form fields
 */

// Email validation
export const validateEmail = (email) => {
  if (!email) {
    return { valid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  // Check for common invalid patterns
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return { valid: false, error: 'Email format is invalid' };
  }
  
  return { valid: true, error: null };
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  if (name.trim().length < 2) {
    return { valid: false, error: `${fieldName} must be at least 2 characters` };
  }
  
  if (name.trim().length > 100) {
    return { valid: false, error: `${fieldName} must be less than 100 characters` };
  }
  
  // Check for valid name characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-'\.]+$/;
  if (!nameRegex.test(name.trim())) {
    return { valid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }
  
  return { valid: true, error: null };
};

// Phone validation (international format)
export const validatePhone = (phone, fieldName = 'Phone') => {
  if (!phone || phone.trim().length === 0) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // Check if it starts with + (international format)
  if (!cleaned.startsWith('+')) {
    return { valid: false, error: `${fieldName} must include country code (e.g., +1 234 567 8900)` };
  }
  
  // Check length (minimum 8 digits after +, maximum 15 total)
  if (cleaned.length < 9 || cleaned.length > 16) {
    return { valid: false, error: `${fieldName} format is invalid. Use international format: +1 234 567 8900` };
  }
  
  // Check for only digits after +
  const digitsOnly = cleaned.substring(1);
  if (!/^\d+$/.test(digitsOnly)) {
    return { valid: false, error: `${fieldName} can only contain numbers after the country code` };
  }
  
  return { valid: true, error: null };
};

// Country validation
export const validateCountry = (country) => {
  if (!country || country.trim().length === 0) {
    return { valid: false, error: 'Country is required' };
  }
  
  return { valid: true, error: null };
};

// City validation
export const validateCity = (city) => {
  if (!city || city.trim().length === 0) {
    return { valid: false, error: 'City is required' };
  }
  
  if (city.trim().length < 2) {
    return { valid: false, error: 'City name must be at least 2 characters' };
  }
  
  if (city.trim().length > 100) {
    return { valid: false, error: 'City name must be less than 100 characters' };
  }
  
  return { valid: true, error: null };
};

// State/Province validation (optional but validated if provided)
export const validateState = (state, required = false) => {
  if (required && (!state || state.trim().length === 0)) {
    return { valid: false, error: 'State/Province is required' };
  }
  
  if (state && state.trim().length > 100) {
    return { valid: false, error: 'State/Province name must be less than 100 characters' };
  }
  
  return { valid: true, error: null };
};

// ZIP/Postal code validation
export const validateZipCode = (zipCode, country = null, required = false) => {
  if (!zipCode || zipCode.trim().length === 0) {
    if (required) {
      return { valid: false, error: 'ZIP/Postal Code is required' };
    }
    return { valid: true, error: null }; // Optional field
  }
  
  // Basic length check
  if (zipCode.trim().length < 3) {
    return { valid: false, error: 'ZIP/Postal code must be at least 3 characters' };
  }
  
  if (zipCode.trim().length > 20) {
    return { valid: false, error: 'ZIP/Postal code must be less than 20 characters' };
  }
  
  // Country-specific validation could be added here
  // For now, accept alphanumeric with common separators
  const zipRegex = /^[A-Z0-9\s\-]+$/i;
  if (!zipRegex.test(zipCode.trim())) {
    return { valid: false, error: 'ZIP/Postal code format is invalid' };
  }
  
  return { valid: true, error: null };
};

// Blood type validation
export const validateBloodType = (bloodType, required = false) => {
  if (required && !bloodType) {
    return { valid: false, error: 'Blood type is required' };
  }
  
  const validBloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  if (bloodType && !validBloodTypes.includes(bloodType)) {
    return { valid: false, error: 'Please select a valid blood type' };
  }
  
  return { valid: true, error: null };
};

// Birth year validation
export const validateBirthYear = (birthYear, required = true) => {
  // Handle null/undefined - now required
  if (required && (birthYear === null || birthYear === undefined)) {
    return { valid: false, error: 'Birth year is required' };
  }
  
  // If not required and not provided, it's valid
  if (!required && (birthYear === null || birthYear === undefined)) {
    return { valid: true, error: null };
  }

  // Convert to string for digit checking
  const yearStr = String(birthYear).trim();
  
  // Empty string is invalid (must be exactly 4 digits if provided)
  if (yearStr === '') {
    return { valid: false, error: 'Birth year must be exactly 4 digits' };
  }

  // Check for exactly 4 digits
  if (!/^\d{4}$/.test(yearStr)) {
    return { valid: false, error: 'Birth year must be exactly 4 digits' };
  }

  const year = parseInt(yearStr, 10);
  
  if (isNaN(year)) {
    return { valid: false, error: 'Birth year must be a valid number' };
  }

  const currentYear = new Date().getFullYear();
  const maxBirthYear = currentYear - 150; // Max 150 years old

  // Check if too old (more than 150 years)
  if (year < maxBirthYear) {
    return { valid: false, error: 'Birth year cannot be more than 150 years ago' };
  }

  // Check if future year
  if (year > currentYear) {
    return { valid: false, error: 'Birth year cannot be in the future' };
  }

  // Calculate age
  const age = currentYear - year;
  const minAge = 18;

  if (age < minAge) {
    return { valid: false, error: 'You must be at least 18 years old to register' };
  }

  return { valid: true, error: null };
};

// Gender validation
export const validateGender = (gender, required = true) => {
  if (required && !gender) {
    return { valid: false, error: 'Gender is required' };
  }
  
  // If not required and not provided, it's valid
  if (!required && !gender) {
    return { valid: true, error: null };
  }

  const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
  if (!validGenders.includes(gender)) {
    return { valid: false, error: 'Please select a valid gender' };
  }

  return { valid: true, error: null };
};

// Organ selection validation
export const validateOrganSelection = (selectedOrgans, required = false) => {
  if (required && (!selectedOrgans || selectedOrgans.length === 0)) {
    return { valid: false, error: 'Please select at least one organ to pledge' };
  }

  return { valid: true, error: null };
};

// Emergency contact validation
export const validateEmergencyContact = (emergencyContact, required = false) => {
  if (!required) {
    return { valid: true, error: null };
  }

  if (!emergencyContact) {
    return { valid: false, error: 'Emergency contact information is required' };
  }

  const name = emergencyContact.name || '';
  const phone = emergencyContact.phone || '';

  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Emergency contact name is required' };
  }

  if (!phone || phone.trim().length === 0) {
    return { valid: false, error: 'Emergency contact phone is required' };
  }

  const nameResult = validateName(name, 'Emergency contact name');
  if (!nameResult.valid) {
    return nameResult;
  }

  const phoneResult = validatePhone(phone, 'Emergency contact phone');
  if (!phoneResult.valid) {
    return phoneResult;
  }

  return { valid: true, error: null };
};

// Units validation (for blood requests)
export const validateUnits = (units) => {
  if (!units) {
    return { valid: false, error: 'Number of units is required' };
  }
  
  const num = parseInt(units, 10);
  if (isNaN(num) || num < 1) {
    return { valid: false, error: 'Units must be at least 1' };
  }
  
  if (num > 100) {
    return { valid: false, error: 'Units cannot exceed 100' };
  }
  
  return { valid: true, error: null };
};

// Urgency level validation
export const validateUrgency = (urgency) => {
  if (!urgency) {
    return { valid: false, error: 'Urgency level is required' };
  }
  
  const validUrgencyLevels = ['low', 'medium', 'high', 'critical'];
  if (!validUrgencyLevels.includes(urgency)) {
    return { valid: false, error: 'Please select a valid urgency level' };
  }
  
  return { valid: true, error: null };
};

// Hospital name validation
export const validateHospital = (hospital) => {
  if (!hospital || hospital.trim().length === 0) {
    return { valid: false, error: 'Hospital name is required' };
  }
  
  if (hospital.trim().length < 3) {
    return { valid: false, error: 'Hospital name must be at least 3 characters' };
  }
  
  if (hospital.trim().length > 255) {
    return { valid: false, error: 'Hospital name must be less than 255 characters' };
  }
  
  return { valid: true, error: null };
};

// Relationship validation
export const validateRelationship = (relationship) => {
  if (!relationship || relationship.trim().length === 0) {
    return { valid: false, error: 'Relationship is required' };
  }
  
  if (relationship.trim().length < 2) {
    return { valid: false, error: 'Relationship must be at least 2 characters' };
  }
  
  if (relationship.trim().length > 100) {
    return { valid: false, error: 'Relationship must be less than 100 characters' };
  }
  
  return { valid: true, error: null };
};

// Password validation
export const validatePassword = (password) => {
  if (!password) {
    return { valid: false, error: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters' };
  }
  
  if (password.length > 128) {
    return { valid: false, error: 'Password must be less than 128 characters' };
  }
  
  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one uppercase letter' };
  }
  
  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one lowercase letter' };
  }
  
  // Check for at least one number
  if (!/\d/.test(password)) {
    return { valid: false, error: 'Password must contain at least one number' };
  }
  
  return { valid: true, error: null };
};

// URL validation
export const validateURL = (url, fieldName = 'URL') => {
  if (!url || url.trim().length === 0) {
    return { valid: true, error: null }; // Optional field
  }
  
  try {
    new URL(url);
    return { valid: true, error: null };
  } catch {
    return { valid: false, error: `${fieldName} must be a valid URL (e.g., https://example.com)` };
  }
};

// Address validation
export const validateAddress = (address, required = false) => {
  if (required && (!address || address.trim().length === 0)) {
    return { valid: false, error: 'Address is required' };
  }
  
  if (address && address.trim().length < 5) {
    return { valid: false, error: 'Address must be at least 5 characters' };
  }
  
  if (address && address.trim().length > 500) {
    return { valid: false, error: 'Address must be less than 500 characters' };
  }
  
  return { valid: true, error: null };
};

// Generic required field validation
export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  return { valid: true, error: null };
};

// Validate all fields in a form object
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;
  
  for (const [field, rules] of Object.entries(validationRules)) {
    const value = formData[field];
    
    for (const rule of rules) {
      const result = rule(value, formData);
      if (!result.valid) {
        errors[field] = result.error;
        isValid = false;
        break; // Stop at first error for this field
      }
    }
  }
  
  return { isValid, errors };
};

